import Typography from '@mui/material/Typography';
import { useVerifyEmail } from 'features/user';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { LoginLogo } from 'shared/components/ui/login-logo';
import { useDelayedCallback } from 'shared/hooks/use-delayed-callback';
import { useNavigateAppOpenScreen } from 'shared/hooks/use-navigate-app-open-screen';
import { useUser } from 'shared/hooks/use-user';
import { QueryParamOptions, useQueryParam } from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';

import * as Styled from './verify-email.style';

export const VerifyEmail: React.FC = () => {
  const { t } = useTranslation();
  const navigateAppOpen = useNavigateAppOpenScreen();
  const [code] = useQueryParam(QueryParamOptions.OobCode);
  const { verifyEmail, isLoading, error, isSuccess } = useVerifyEmail();
  const user = useUser();
  const [redirectToLogin] = useDelayedCallback(navigateAppOpen, 5000);

  useEffect(() => {
    verifyEmail(code);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on first render
  }, []);

  useEffect(() => {
    if (isSuccess) {
      redirectToLogin(user ?? null);
    }
  }, [isSuccess, redirectToLogin, user]);

  const content = useMemo(() => {
    if (isLoading) {
      return <Styled.Loader size={40} />;
    }

    if (error) {
      return (
        <Typography variant="overline" textAlign="center">
          {t('forms.verify-email.error.message')}
        </Typography>
      );
    }

    if (isSuccess) {
      return (
        <>
          <Typography variant="overline" textAlign="center">
            {t('forms.verify-email.success.message')}
          </Typography>
          <Styled.Anchor to={Paths.Home}>
            {t('forms.verify-email.success.redirect.label')}
          </Styled.Anchor>
        </>
      );
    }
  }, [error, isLoading, isSuccess, t]);

  return (
    <Styled.Container>
      <Styled.Body>
        <Styled.BodyContent>
          <LoginLogo />

          <Typography variant="h1" textAlign="center">
            {t('forms.verify-email.title')}
          </Typography>

          {content}
        </Styled.BodyContent>
      </Styled.Body>
    </Styled.Container>
  );
};
